import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Chip,
  LinearProgress,
  Tooltip,
  Alert,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import SyncAltOutlinedIcon from '@mui/icons-material/SyncAltOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { CmsProject, CmsGalleryItem, CmsDocumentItem } from '../../types/cms';
import { WebsiteCmsService } from '../../services/websiteCmsService';

const GOLD = '#9d6f32';
const BORDER = '#e8e8e8';

interface ProjectGalleryTabProps {
  project: CmsProject;
  onChange: (updated: CmsProject) => void;
}

export const ProjectGalleryTab: React.FC<ProjectGalleryTabProps> = ({ project, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState<number | null>(null);
  const [uploadCategory, setUploadCategory] = useState<'renders' | 'actual' | 'floor-plans' | 'video-tours'>('renders');

  const handleConstructionVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 50 * 1024 * 1024) {
      const proceed = window.confirm(
        `This video is ${(file.size / (1024 * 1024)).toFixed(1)}MB. Larger videos take longer to stream for mobile visitors. Proceed with upload?`
      );
      if (!proceed) {
        e.target.value = '';
        return;
      }
    }

    setUploading(true);
    setVideoUploadProgress(0);
    try {
      const url = await WebsiteCmsService.uploadMedia(
        file,
        project.slug,
        'construction',
        (progress) => setVideoUploadProgress(progress)
      );
      onChange({
        ...project,
        constructionVideoUrl: url,
        constructionVideoDate: project.constructionVideoDate || new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      });
    } catch (err) {
      console.error('Failed to upload construction video', err);
      alert('Failed to upload construction video. Please check your network and try again.');
    } finally {
      setUploading(false);
      setVideoUploadProgress(null);
      e.target.value = '';
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const newGalleryItems: CmsGalleryItem[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const url = await WebsiteCmsService.uploadMedia(file, project.slug, uploadCategory);
        newGalleryItems.push({
          id: `g_${Date.now()}_${i}`,
          src: url,
          alt: `${project.name} - ${uploadCategory}`,
          category: uploadCategory,
          caption: file.name.replace(/\.[^/.]+$/, ''),
        });
      }
      onChange({
        ...project,
        gallery: [...project.gallery, ...newGalleryItems],
      });
    } catch (err) {
      console.error('Failed to upload images', err);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleReplaceHeroImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await WebsiteCmsService.uploadMedia(file, project.slug, 'hero');
      onChange({ ...project, heroImage: url });
    } catch (err) {
      console.error('Failed to replace hero image', err);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleReplaceThumbnailImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await WebsiteCmsService.uploadMedia(file, project.slug, 'thumb');
      onChange({ ...project, thumbnailImage: url });
    } catch (err) {
      console.error('Failed to replace thumbnail image', err);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleReplaceGalleryImage = async (id: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await WebsiteCmsService.uploadMedia(file, project.slug, 'gallery');
      onChange({
        ...project,
        gallery: project.gallery.map((item) => (item.id === id ? { ...item, src: url } : item)),
      });
    } catch (err) {
      console.error('Failed to replace gallery image', err);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRemoveGalleryItem = (id: string) => {
    onChange({
      ...project,
      gallery: project.gallery.filter((item) => item.id !== id),
    });
  };

  const handleGalleryCaptionChange = (id: string, caption: string) => {
    onChange({
      ...project,
      gallery: project.gallery.map((item) => (item.id === id ? { ...item, caption } : item)),
    });
  };

  const handleGalleryCategoryChange = (id: string, category: 'renders' | 'actual' | 'floor-plans' | 'video-tours') => {
    onChange({
      ...project,
      gallery: project.gallery.map((item) => (item.id === id ? { ...item, category } : item)),
    });
  };

  const handleAddDocument = () => {
    const newDoc: CmsDocumentItem = {
      id: `d_${Date.now()}`,
      label: 'New Compliance Document',
      description: 'Official approved document',
      filename: `${project.slug}-document.pdf`,
      fileUrl: '',
    };
    onChange({
      ...project,
      documents: [...(project.documents || []), newDoc],
    });
  };

  const handleRemoveDocument = (id: string) => {
    onChange({
      ...project,
      documents: (project.documents || []).filter((d) => d.id !== id),
    });
  };

  const handleDocChange = (id: string, field: keyof CmsDocumentItem, val: string) => {
    onChange({
      ...project,
      documents: (project.documents || []).map((d) => (d.id === id ? { ...d, [field]: val } : d)),
    });
  };

  const handleDocFileUpload = async (id: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await WebsiteCmsService.uploadMedia(file, project.slug, 'documents');
      onChange({
        ...project,
        documents: (project.documents || []).map((d) =>
          d.id === id ? { ...d, fileUrl: url, filename: file.name } : d
        ),
      });
    } catch (err) {
      console.error('Failed to upload document', err);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
      {uploading && <LinearProgress sx={{ color: GOLD, mb: -2 }} />}

      {/* 1. Hero & Thumbnail Images */}
      <Paper sx={{ p: 3, borderRadius: 2, border: `1px solid ${BORDER}`, boxShadow: 'none' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, color: '#1a1a1a' }}>
          🌟 Primary Banner & Project Thumbnail
        </Typography>
        <Typography variant="body2" sx={{ color: '#6b6b6b', mb: 2.5 }}>
          The hero image is the full-width header on the project page; thumbnail appears on project listing cards.
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          {/* Hero Banner */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Hero Banner Image (1920×1080)
              </Typography>
              <Button
                variant="outlined"
                component="label"
                size="small"
                startIcon={<DriveFolderUploadOutlinedIcon />}
                disabled={uploading}
                sx={{
                  borderColor: GOLD,
                  color: GOLD,
                  textTransform: 'none',
                  fontSize: 12,
                  py: 0.25,
                  fontWeight: 700,
                  '&:hover': { bgcolor: 'rgba(157,111,50,0.06)' },
                }}
              >
                Replace Image
                <input type="file" accept="image/*" hidden onChange={handleReplaceHeroImage} />
              </Button>
            </Box>

            <TextField
              fullWidth
              size="small"
              value={project.heroImage}
              onChange={(e) => onChange({ ...project, heroImage: e.target.value })}
              placeholder="https://..."
              sx={{ mb: 1.5 }}
            />

            {project.heroImage && (
              <Box
                component="img"
                src={project.heroImage}
                alt="Hero Banner Preview"
                sx={{
                  width: '100%',
                  height: 180,
                  objectFit: 'cover',
                  borderRadius: 1.5,
                  border: `1px solid ${BORDER}`,
                }}
              />
            )}
          </Box>

          {/* Thumbnail Card */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Thumbnail Card Image (800×1000)
              </Typography>
              <Button
                variant="outlined"
                component="label"
                size="small"
                startIcon={<DriveFolderUploadOutlinedIcon />}
                disabled={uploading}
                sx={{
                  borderColor: GOLD,
                  color: GOLD,
                  textTransform: 'none',
                  fontSize: 12,
                  py: 0.25,
                  fontWeight: 700,
                  '&:hover': { bgcolor: 'rgba(157,111,50,0.06)' },
                }}
              >
                Replace Image
                <input type="file" accept="image/*" hidden onChange={handleReplaceThumbnailImage} />
              </Button>
            </Box>

            <TextField
              fullWidth
              size="small"
              value={project.thumbnailImage}
              onChange={(e) => onChange({ ...project, thumbnailImage: e.target.value })}
              placeholder="https://..."
              sx={{ mb: 1.5 }}
            />

            {project.thumbnailImage && (
              <Box
                component="img"
                src={project.thumbnailImage}
                alt="Thumbnail Preview"
                sx={{
                  width: '100%',
                  height: 180,
                  objectFit: 'cover',
                  borderRadius: 1.5,
                  border: `1px solid ${BORDER}`,
                }}
              />
            )}
          </Box>
        </Box>
      </Paper>

      {/* 2. Construction Progress Update Video (Cinematic Dark Section) */}
      <Paper
        sx={{
          p: 3,
          borderRadius: 2,
          border: `1px solid ${BORDER}`,
          boxShadow: 'none',
          background: 'linear-gradient(180deg, #ffffff 0%, #fbf9f6 100%)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <VideocamOutlinedIcon sx={{ color: GOLD, fontSize: 24 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                Construction Progress Update Video (Cinematic Section)
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#6b6b6b' }}>
              Powers the cinematic dark section (<strong>"See the progress."</strong>) on the live website. Shows actual site progress to build buyer trust.
            </Typography>
          </Box>

          <Button
            variant="contained"
            component="label"
            size="small"
            startIcon={<DriveFolderUploadOutlinedIcon />}
            disabled={uploading}
            sx={{
              bgcolor: GOLD,
              color: '#fff',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: 13,
              '&:hover': { bgcolor: '#865e2b' },
            }}
          >
            {project.constructionVideoUrl ? 'Replace Video' : 'Upload Construction Video'}
            <input type="file" accept="video/mp4,video/webm,video/quicktime" hidden onChange={handleConstructionVideoUpload} />
          </Button>
        </Box>

        {videoUploadProgress !== null && (
          <Box sx={{ mb: 2.5, p: 2, bgcolor: '#f5efe6', borderRadius: 1.5, border: '1px solid rgba(157,111,50,0.2)' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: GOLD }}>
                Uploading Video to Cloud Storage...
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 700, color: GOLD }}>
                {videoUploadProgress}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={videoUploadProgress}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: 'rgba(157,111,50,0.15)',
                '& .MuiLinearProgress-bar': { bgcolor: GOLD },
              }}
            />
          </Box>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 2, mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Milestone / Date Badge"
            value={project.constructionVideoDate || ''}
            onChange={(e) => onChange({ ...project, constructionVideoDate: e.target.value })}
            placeholder="e.g. January 2025 or Slab 14 Completed"
            helperText="Appears as a glowing gold badge above the video player"
            InputProps={{
              startAdornment: <CalendarTodayOutlinedIcon sx={{ fontSize: 18, color: '#888', mr: 1 }} />,
            }}
          />

          <TextField
            fullWidth
            size="small"
            label="Video File URL (Direct MP4 / WebM)"
            value={project.constructionVideoUrl || ''}
            onChange={(e) => onChange({ ...project, constructionVideoUrl: e.target.value })}
            placeholder="https://firebasestorage.googleapis.com/... or /videos/..."
            helperText="Auto-populated when you upload a video, or paste a direct CDN link"
          />
        </Box>

        {/* Video Player Preview */}
        {project.constructionVideoUrl ? (
          <Box
            sx={{
              mt: 2,
              p: 2.5,
              borderRadius: 2,
              bgcolor: '#111111',
              color: '#ffffff',
              border: '1px solid #222222',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="caption" sx={{ color: GOLD, letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 700, fontSize: 11 }}>
                  Live Website Preview
                </Typography>
                {project.constructionVideoDate && (
                  <Chip
                    label={project.constructionVideoDate}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(157,111,50,0.2)',
                      color: '#c8a97a',
                      border: '1px solid rgba(157,111,50,0.4)',
                      fontWeight: 700,
                      fontSize: 11,
                      height: 22,
                    }}
                  />
                )}
              </Box>

              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => onChange({ ...project, constructionVideoUrl: '', constructionVideoDate: '' })}
                sx={{
                  fontSize: 11,
                  textTransform: 'none',
                  py: 0.25,
                  borderColor: 'rgba(239, 68, 68, 0.4)',
                  color: '#f87171',
                  '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.1)', borderColor: '#ef4444' },
                }}
              >
                Remove Video Section
              </Button>
            </Box>

            <Box sx={{ borderRadius: 1.5, overflow: 'hidden', bgcolor: '#000000', maxHeight: 360, display: 'flex', justifyContent: 'center' }}>
              <video
                src={project.constructionVideoUrl}
                controls
                playsInline
                preload="metadata"
                style={{ width: '100%', maxHeight: 360, objectFit: 'contain' }}
              />
            </Box>
          </Box>
        ) : (
          <Alert severity="info" sx={{ mt: 1, bgcolor: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd', fontSize: 13 }}>
            No construction video is currently active. Upload an MP4 video or paste a video URL above to enable the cinematic "See the progress" section on the project page.
          </Alert>
        )}
      </Paper>

      {/* 3. Upload & Categorized Gallery */}
      <Paper sx={{ p: 3, borderRadius: 2, border: `1px solid ${BORDER}`, boxShadow: 'none' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
              📸 Media Gallery & Architectural Plans
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b6b6b' }}>
              Click "Replace" on any card to update its photo directly from your device.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Select
              size="small"
              value={uploadCategory}
              onChange={(e) => setUploadCategory(e.target.value as any)}
              sx={{ minWidth: 150, fontSize: 13 }}
            >
              <MenuItem value="renders">3D Renders</MenuItem>
              <MenuItem value="actual">Actual Site Photos</MenuItem>
              <MenuItem value="floor-plans">Floor Plans</MenuItem>
              <MenuItem value="video-tours">Video Tours</MenuItem>
            </Select>

            <Button
              variant="contained"
              component="label"
              size="small"
              startIcon={<CloudUploadOutlinedIcon />}
              disabled={uploading}
              sx={{
                bgcolor: GOLD,
                color: '#fff',
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': { bgcolor: '#865e2b' },
              }}
            >
              Upload Images
              <input type="file" multiple accept="image/*" hidden onChange={handleFileUpload} />
            </Button>
          </Box>
        </Box>

        {project.gallery.length === 0 ? (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              border: `2px dashed ${BORDER}`,
              borderRadius: 2,
              bgcolor: '#fafafa',
            }}
          >
            <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 48, color: '#9ca3af', mb: 1 }} />
            <Typography variant="body2" sx={{ color: '#6b6b6b', fontWeight: 600 }}>
              No gallery images uploaded yet. Select a category and click "Upload Images" to populate.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
            {project.gallery.map((item) => (
              <Box
                key={item.id}
                sx={{
                  position: 'relative',
                  border: `1px solid ${BORDER}`,
                  borderRadius: 1.5,
                  overflow: 'hidden',
                  bgcolor: '#fff',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                }}
              >
                {/* Image Preview */}
                <Box
                  component="img"
                  src={item.src}
                  alt={item.alt}
                  onError={(e: any) => {
                    e.currentTarget.src = 'https://picsum.photos/seed/placeholder-house/800/600';
                  }}
                  sx={{ width: '100%', height: 150, objectFit: 'cover', display: 'block', bgcolor: '#f3f4f6' }}
                />

                {/* Card Controls */}
                <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {/* Category Selector */}
                    <Select
                      size="small"
                      value={item.category}
                      onChange={(e) => handleGalleryCategoryChange(item.id, e.target.value as any)}
                      sx={{
                        fontSize: 11,
                        height: 24,
                        bgcolor: '#f3f4f6',
                        '& .MuiSelect-select': { py: 0.25, px: 1 },
                      }}
                    >
                      <MenuItem value="renders" sx={{ fontSize: 12 }}>Renders</MenuItem>
                      <MenuItem value="actual" sx={{ fontSize: 12 }}>Actual Photos</MenuItem>
                      <MenuItem value="floor-plans" sx={{ fontSize: 12 }}>Floor Plans</MenuItem>
                      <MenuItem value="video-tours" sx={{ fontSize: 12 }}>Video Tours</MenuItem>
                    </Select>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {/* Direct Replace Image Button */}
                      <Tooltip title="Replace image directly with file from your device">
                        <IconButton
                          component="label"
                          size="small"
                          disabled={uploading}
                          sx={{
                            color: GOLD,
                            bgcolor: 'rgba(157,111,50,0.08)',
                            p: 0.5,
                            '&:hover': { bgcolor: 'rgba(157,111,50,0.18)' },
                          }}
                        >
                          <SyncAltOutlinedIcon sx={{ fontSize: 16 }} />
                          <input
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={(e) => handleReplaceGalleryImage(item.id, e)}
                          />
                        </IconButton>
                      </Tooltip>

                      {/* Delete Button */}
                      <Tooltip title="Delete image">
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveGalleryItem(item.id)}
                          sx={{ color: '#ef4444', p: 0.5, '&:hover': { bgcolor: '#fee2e2' } }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {/* Caption Field */}
                  <TextField
                    size="small"
                    placeholder="Caption / Description"
                    fullWidth
                    value={item.caption || ''}
                    onChange={(e) => handleGalleryCaptionChange(item.id, e.target.value)}
                    sx={{ '& .MuiInputBase-input': { fontSize: 11.5, py: 0.5 } }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      {/* 4. Official Documents & Brochures */}
      <Paper sx={{ p: 3, borderRadius: 2, border: `1px solid ${BORDER}`, boxShadow: 'none' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
              📄 Official Brochures & Legal Documents
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b6b6b' }}>
              Upload and manage downloadable RERA certificates, NA orders, and project brochures.
            </Typography>
          </Box>

          <Button
            variant="outlined"
            size="small"
            startIcon={<PictureAsPdfOutlinedIcon />}
            onClick={handleAddDocument}
            sx={{
              borderColor: GOLD,
              color: GOLD,
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': { bgcolor: 'rgba(157,111,50,0.06)' },
            }}
          >
            Add Document
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Main Brochure URL (PDF)"
            fullWidth
            size="small"
            value={project.brochureUrl || ''}
            onChange={(e) => onChange({ ...project, brochureUrl: e.target.value })}
            placeholder="https://firebasestorage.googleapis.com/..."
            helperText="Direct link to the downloadable project brochure"
          />

          {(project.documents || []).map((docItem) => (
            <Box
              key={docItem.id}
              sx={{
                p: 2,
                borderRadius: 1.5,
                border: `1px solid ${BORDER}`,
                bgcolor: '#fafafa',
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '2fr 3fr 2fr auto auto' },
                gap: 2,
                alignItems: 'center',
              }}
            >
              <TextField
                size="small"
                label="Document Label"
                value={docItem.label}
                onChange={(e) => handleDocChange(docItem.id, 'label', e.target.value)}
              />
              <TextField
                size="small"
                label="Description"
                value={docItem.description}
                onChange={(e) => handleDocChange(docItem.id, 'description', e.target.value)}
              />
              <TextField
                size="small"
                label="PDF Filename / URL"
                value={docItem.fileUrl || docItem.filename}
                onChange={(e) => handleDocChange(docItem.id, 'fileUrl', e.target.value)}
              />
              <Button
                variant="outlined"
                component="label"
                size="small"
                startIcon={<DriveFolderUploadOutlinedIcon />}
                disabled={uploading}
                sx={{
                  borderColor: GOLD,
                  color: GOLD,
                  textTransform: 'none',
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                Upload PDF
                <input
                  type="file"
                  accept="application/pdf"
                  hidden
                  onChange={(e) => handleDocFileUpload(docItem.id, e)}
                />
              </Button>
              <IconButton
                size="small"
                onClick={() => handleRemoveDocument(docItem.id)}
                sx={{ color: '#ef4444' }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      </Paper>
    </Box>
  );
};
